import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';

interface SessionExpiryDialogProps {
  open: boolean;
  onClose?: () => void;
}

/**
 * Shown when the session can no longer be refreshed in the background. The
 * person decides: try to keep the session, or sign out (and back in). It
 * can't be dismissed without choosing, since the page behind it would only
 * fail.
 */
const SessionExpiryDialog: React.FC<SessionExpiryDialogProps> = ({ open, onClose }) => {
  const { refreshSession, handleSessionExpiry } = useAuth();
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  // The dialog stays mounted between sessions: a later expiry starts clean.
  useEffect(() => {
    if (!open) setFailed(false);
  }, [open]);

  const handleStaySignedIn = async () => {
    try {
      setLoading(true);
      setFailed(false);

      await refreshSession();

      // Close dialog on successful refresh
      onClose?.();
    } catch (error: unknown) {
      console.error('Failed to refresh session:', error);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = () => {
    setFailed(false);
    handleSessionExpiry();
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth="xs"
      fullWidth
      aria-labelledby="session-expiry-title"
      aria-describedby="session-expiry-description"
    >
      <DialogTitle id="session-expiry-title" sx={{ fontWeight: 700 }}>
        Your session expired
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="session-expiry-description" sx={{ color: 'text.secondary' }}>
          For your security, PithPlay signs you out after a while. Stay signed in to keep working, or sign out.
        </DialogContentText>
        {failed && (
          <Alert severity="error" sx={{ mt: 2 }}>
            We couldn't restore your session. Try again, or sign out and sign back in.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleSignOut} disabled={loading} color="inherit">
          Sign out
        </Button>
        <Button onClick={handleStaySignedIn} variant="contained" disabled={loading}>
          {loading ? (
            <>
              <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} aria-hidden="true" />
              Restoring…
            </>
          ) : failed ? (
            'Try again'
          ) : (
            'Stay signed in'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionExpiryDialog;
